import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import groupBy from 'lodash-es/groupBy';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import trim from 'lodash-es/trim';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { RouterTypeDto } from '../../../api/core';
import * as fromRouterTypes from '../../../router-types';
import { DeviceErrorCollection, DeviceErrors, ErrorKeys, RouterCreation } from '../../devices.models';
import { CSVHeader } from './../../devices.models';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { clearEmptyValue } from '../../../utils';

export enum ImportColumns {
    ROUTER_TYPE = 'ROUTER_TYPE',
    SERIAL_NUMBER = 'SERIAL_NUMBER',
    IMEI = 'IMEI',
    ICCID = 'ICCID',
    FW_VERSION = 'FW_VERSION',
    SIM_EXPIRATION_DATE = 'SIM_EXPIRATION_DATE',
    VENDOR = 'VENDOR',
}

@Component({
    selector: 'liebherr-add-devices-dialog',
    templateUrl: 'add-devices-dialog.component.html',
    styleUrls: ['add-devices-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDevicesDialogComponent extends SubscriptionComponent {
    @ViewChild('uploader') set uploader(el: ElementRef) {
        this.uploaderRef$.next(el);
    }

    uploaderRef$ = new BehaviorSubject<ElementRef | undefined>(undefined);
    deviceList$ = new BehaviorSubject<{ device: RouterCreation; errors: Partial<DeviceErrors> }[]>([]);
    hasErrors$: Observable<boolean>;
    processing$ = new BehaviorSubject<boolean>(false);

    routerTypes$: Observable<RouterTypeDto[]> = this.store.select(fromRouterTypes.selectRouterTypes);
    routerTypeOptions$: Observable<{ vendor: string; routerTypes: RouterTypeDto[] }[]> = this.routerTypes$.pipe(
        map((routerTypes) => this.getRouterTypeOptions(routerTypes))
    );

    headerWarning$ = new BehaviorSubject<string[] | undefined>(undefined);
    missingOptionalHeaders$ = new BehaviorSubject<string | undefined>(undefined);

    CSV_HEADER = CSVHeader;
    IMPORT_COLUMNS = ImportColumns;
    errors$: Observable<DeviceErrorCollection>;

    singleForm = this.fb.group({
        routerType: [undefined, Validators.required],
        serialNumber: [undefined, [Validators.required, this.validateSerialNumber]],
        id: [undefined, [Validators.required, this.validateIMEI]],
        iccid: [undefined, [this.validateICCID]],
    });
    displayedColumns$ = new BehaviorSubject<ImportColumns[]>([
        ImportColumns.ROUTER_TYPE,
        ImportColumns.SERIAL_NUMBER,
        ImportColumns.IMEI,
        ImportColumns.FW_VERSION,
        ImportColumns.VENDOR,
    ]);
    optionalHeaders = [CSVHeader.SIMEXPIRATIONDATE, CSVHeader.ICCID];

    ERROR_KEYS = ErrorKeys;

    constructor(public dialogRef: MatDialogRef<AddDevicesDialogComponent>, private fb: UntypedFormBuilder, private store: Store) {
        super();

        this.hasErrors$ = this.deviceList$.pipe(
            map((devices) => {
                return devices?.filter((d) => !isEmpty(d.errors)).length > 0;
            })
        );

        // per error key the count of errors is collected to be displayed in the corresponding column
        this.errors$ = this.deviceList$.pipe(
            map((devices) => {
                const errors: DeviceErrorCollection = {};

                Object.keys(ErrorKeys).forEach((key) => {
                    errors[key] = devices?.filter((d) => d.errors[key]).length;
                });

                return errors;
            })
        );
    }

    onCancel() {
        this.dialogRef.close();
    }
    onSubmit(deviceList: { device: RouterCreation; errors: Partial<DeviceErrors> }[], hasErrors: boolean) {
        if (deviceList.length > 0) {
            // csv import
            if (!hasErrors) {
                this.dialogRef.close(deviceList.map((d) => new RouterCreation(d.device)));
            }
        } else {
            // single form
            if (this.singleForm.valid && this.singleForm.dirty) {
                const formValue = this.singleForm.getRawValue();
                this.dialogRef.close(
                    new RouterCreation({
                        ...formValue,
                        routerType: formValue.routerType?.name,
                        vendor: formValue.routerType?.vendor,
                    })
                );
            }
        }
    }

    onFilesChanged(files: FileList, routerTypes: RouterTypeDto[]) {
        this.deviceList$.next([]);
        const filePromise = new Promise<string | ArrayBuffer>((resolve) => {
            const fileReader = new FileReader();
            fileReader.onload = (event: any) => {
                if (event.target && event.target.result) {
                    resolve(event.target.result);
                }
            };
            if (files[0]) {
                this.processing$.next(true);
                fileReader.readAsText(files[0]);
            }
        });
        filePromise.then((res) => {
            const mappedCSV = this.mapCsvToList(res as string);

            const invalidHeaders = this.validateHeaders(mappedCSV.headers);

            if (invalidHeaders.length > 0) {
                // display header warning
                this.processing$.next(false);
                this.headerWarning$.next(invalidHeaders);
                this.headerWarning$.next(invalidHeaders);
            } else {
                // update displayed columns
                this.updateDynamicColumns(mappedCSV.headers);
                // reset header warning
                this.headerWarning$.next(undefined);
                // map items
                const mappedDevices = mappedCSV.items.map((i) => new RouterCreation(i));

                // get list of all serial numbers
                const allSerialNumbers = mappedDevices
                    .map((m) => m.serialNumber)
                    .filter((m) => m !== undefined)
                    // tslint:disable-next-line:no-non-null-assertion
                    .map((m) => m!);
                // get list of all imeis
                const allIMEIS = mappedDevices
                    .map((m) => m.id)
                    .filter((m) => m !== undefined)
                    // tslint:disable-next-line:no-non-null-assertion
                    .map((m) => m!);
                // get list of all iccids
                const allICCIDS = mappedDevices
                    .map((m) => m.iccid)
                    .filter((m) => m !== undefined)
                    // tslint:disable-next-line:no-non-null-assertion
                    .map((m) => m!);
                // add devices to list and validate them
                this.processing$.next(false);
                this.deviceList$.next(
                    mappedDevices.map((device) => ({
                        device,
                        errors: {
                            ...this.validateRouterType(device.routerType, device.routerVendor, routerTypes),
                            ...this.validateSerialNumber({ value: device.serialNumber }, allSerialNumbers),
                            ...this.validateIMEI({ value: device.id }, allIMEIS),
                            ...this.validateICCID({ value: device.iccid }, allICCIDS),
                            ...this.validateDate({ value: device.simExpirationDate }),
                            ...this.validateVendor(device.routerVendor, device.routerType, routerTypes),
                        },
                    }))
                );
                this.processing$.next(false);
            }
        });
    }

    mapCsvToList(csv: string) {
        const lines = csv.split('\n');
        const seperator = lines[0].indexOf(';') > -1 ? ';' : ',';
        // IMPORTANT: trim header values!
        const headers = lines[0].split(seperator).map((h) => trim(h));
        const items: any[] = [];

        for (let i = 1; i < lines.length; i++) {
            const currentline = lines[i].split(seperator);

            if (currentline.length > 1) {
                const obj = {};
                for (let j = 0; j < headers.length; j++) {
                    obj[headers[j]] = currentline[j];
                }
                items.push(obj);
            }
        }
        return { headers, items };
    }

    onReupload() {
        this.uploaderRef$
            .pipe(
                filter((ref) => ref !== undefined),
                first()
            )
            .subscribe((ref) => ref?.nativeElement.click());
    }

    private validateHeaders(headers: string[]) {
        const missingHeaders = Object.values(CSVHeader).filter((value) => {
            const headerVal = value;
            const headerMatch = headers.find((h) => h === headerVal);
            return !headerMatch;
        });

        // check missing optional headers
        if (missingHeaders.findIndex((h) => this.optionalHeaders.includes(h)) > -1) {
            this.missingOptionalHeaders$.next(missingHeaders.filter((h) => this.optionalHeaders.includes(h)).join(', '));
        } else {
            this.missingOptionalHeaders$.next(undefined);
        }

        return missingHeaders.filter((v) => !this.optionalHeaders.includes(v));
    }

    private validateSerialNumber(control: AbstractControl | { value: string }, allItems?: string[]) {
        let error: Partial<DeviceErrors> = null;
        // validate format
        const cleanValue = clearEmptyValue(control.value);
        if (isNil(cleanValue)) {
            error = { [ErrorKeys.serialNumber]: true };
        }
        // check for duplicates
        if (!isNil(allItems) && allItems?.filter((i) => i === control.value).length > 1) {
            error = { [ErrorKeys.serialNumberDuplicate]: true };
        }

        return error;
    }

    private validateIMEI(control: AbstractControl | { value: string }, allItems?: string[]) {
        let error: Partial<DeviceErrors> = null;
        // validate format
        const cleanValue = clearEmptyValue(control.value);
        if (isNil(cleanValue)) {
            error = { [ErrorKeys.imei]: true };
        }
        // check for duplicates
        if (!isNil(allItems) && allItems?.filter((i) => i === control.value).length > 1) {
            error = { [ErrorKeys.imeiDuplicate]: true };
        }
        return error;
    }

    private validateICCID(control: { value: string }, allItems?: string[]) {
        let error: Partial<DeviceErrors> = null;
        const cleanValue: string = !isNil(control.value) && control.value !== '' ? trim(control.value) : undefined;
        if (!isNil(cleanValue) && control.value?.match(/^[0-9]{17,21}$/) === null) {
            // validate format
            error = { [ErrorKeys.iccid]: true };
        }
        // check for duplicates
        if (!isNil(allItems) && allItems?.filter((i) => i === control.value).length > 1) {
            error = { [ErrorKeys.iccidDuplicate]: true };
        }
        return error;
    }

    private validateDate(control: { value: string }) {
        let error: Partial<DeviceErrors> = null;
        const cleanValue = !isNil(control.value) ? trim(control.value) : undefined;
        // should be optional, it only should be validated when the value is not empty
        if (cleanValue !== undefined && control.value?.match(/^(0[1-9]|[12][0-9]|3[01])[.](0[1-9]|1[012])[.][0-9]{4}$/) === null) {
            error = { [ErrorKeys.date]: true };
        }
        return error;
    }
    private validateVendor(vendor?: string, routerType?: string, routerTypes?: RouterTypeDto[]) {
        let error: Partial<DeviceErrors> = null;

        // the vendor is required
        const cleanValue = clearEmptyValue(vendor);
        if (isNil(cleanValue)) {
            error = { [ErrorKeys.vendor]: true };
        } else {
            // the vendor must match the router type
            const routerTypeMatch = routerTypes?.find((r) => r.name === routerType);
            const vendorMatchesRouterType = cleanValue?.toLowerCase() === routerTypeMatch?.vendor?.toLowerCase();
            if (!vendorMatchesRouterType) {
                error = { [ErrorKeys.vendorRouterTypeMatch]: true };
            }
        }
        return error;
    }
    private validateRouterType(routerType?: string, vendor?: string, routerTypes?: RouterTypeDto[]) {
        let error: Partial<DeviceErrors> = null;

        // router type is required
        const cleanValue = clearEmptyValue(routerType);
        if (isNil(cleanValue)) {
            error = { [ErrorKeys.routerTypeRequired]: true };
        }
        const routerTypeMatch = routerTypes?.find((r) => r.name === cleanValue);
        // check if router type matches the vendor
        const routerTypeMatchesVendor = !isNil(vendor) && vendor?.toLowerCase() === routerTypeMatch?.vendor?.toLowerCase();
        if (!isNil(cleanValue) && !routerTypeMatchesVendor) {
            error = { [ErrorKeys.routerTypeVendorMatch]: true };
        }
        // check if router type exists
        if (!isNil(cleanValue) && isNil(routerTypeMatch)) {
            error = { [ErrorKeys.routerTypeInvalid]: true };
        }

        return error;
    }

    private updateDynamicColumns(headers: string[]) {
        const current = this.displayedColumns$.getValue();

        this.optionalHeaders
            .map((csvHeader) => {
                return {
                    csvHeader,
                    importHeader: {
                        [CSVHeader.ICCID]: ImportColumns.ICCID,
                        [CSVHeader.SIMEXPIRATIONDATE]: ImportColumns.SIM_EXPIRATION_DATE,
                    }[csvHeader],
                };
            })
            .forEach((header) => {
                const idx = current.indexOf(header.importHeader);
                // when csv headers contain 'sim expiration date' add column to displayedColumns else remove it
                if (headers.includes(header.csvHeader)) {
                    if (idx === -1) {
                        current.push(header.importHeader);
                    }
                } else {
                    if (idx > -1) {
                        current.splice(idx, 1);
                    }
                }
            });
        this.displayedColumns$.next(current);
    }

    private getRouterTypeOptions(routerTypes: RouterTypeDto[]) {
        const vendors = groupBy(routerTypes, (t) => t.vendor || 'UNKNOWN');
        return Object.keys(vendors)
            .filter((k) => k !== 'UNKNOWN')
            .map((key) => ({
                vendor: key,
                routerTypes: vendors[key],
            }));
    }
}
